// Profile Card Component
// Displays user profile information with inline editable initials

import React, { useState, useEffect } from 'react';
import { RevoltInput } from '../../../components/RevoltInput';

export function ProfileCard({ user, onSaveProfile }) {
  const [initials, setInitials] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [originalInitials, setOriginalInitials] = useState('');

  useEffect(() => {
    // Auto-populate initials if they exist
    const userInitials = user?.initials || '';
    setInitials(userInitials);
    setOriginalInitials(userInitials);
  }, [user]);

  const handleInitialsClick = () => {
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    // Force uppercase and limit to 3 characters, letters only
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    setInitials(value);
  };

  const handleInputBlur = () => {
    // Save changes when focus is lost
    const trimmedInitials = initials.trim();

    if (trimmedInitials !== originalInitials) {
      // Only save if there are changes
      const profilePayload = {
        initials: trimmedInitials,
        // Future: username, email, etc.
      };

      if (onSaveProfile) {
        onSaveProfile(profilePayload);
      }

      setOriginalInitials(trimmedInitials);
    }

    setIsEditing(false);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // Trigger blur to save
    } else if (e.key === 'Escape') {
      // Cancel changes
      setInitials(originalInitials);
      setIsEditing(false);
    }
  };

  return (
    <div className="account-card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fa-solid fa-user"></i> Profile Information
        </h2>
      </div>
      <div className="card-body">
        <div className="info-row">
          <span className="info-label">Username:</span>
          <span className="info-value">{user?.username || 'Loading...'}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Initials:</span>
          {isEditing ? (
            <div className="initials-input-container">
              <RevoltInput
                type="text"
                placeholder="e.g., JD, ABC"
                value={initials}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                autoFocus
              />
            </div>
          ) : (
            <span
              className={`info-value initials-display ${!user ? 'disabled' : ''}`}
              onClick={user ? handleInitialsClick : undefined}
              title={!user ? 'Loading...' : 'Click to edit initials'}
            >
              {initials || (!user ? 'Loading...' : 'Click to set initials')}
            </span>
          )}
        </div>
        <div className="info-row">
          <span className="info-label">Email:</span>
          <span className="info-value">{user?.email || 'Loading...'}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Email Status:</span>
          <span className={`info-badge ${user?.emailVerified ? 'success' : 'warning'}`}>
            {user?.emailVerified ? (
              <><i className="fa-solid fa-check-circle"></i> Verified</>
            ) : (
              <><i className="fa-solid fa-exclamation-circle"></i> Not Verified</>
            )}
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Role:</span>
          <span className="info-value">{user?.role === 'admin' ? '👑 Admin' : 'User'}</span>
        </div>
      </div>
    </div>
  );
}
